import speciesJson from "../generated/species.json" with { type: "json" };
import movesJson from "../generated/moves.json" with { type: "json" };
import itemsJson from "../generated/items.json" with { type: "json" };
import machinesJson from "../generated/machines.json" with { type: "json" };
import mapsJson from "../generated/maps.json" with { type: "json" };
import type { ItemRecord, MachineRecord, MapRecord, MoveRecord, SpeciesInfo, SpeciesRecord } from "./types.js";

const SPECIES_RECORDS = speciesJson as unknown as SpeciesRecord[];
const MOVES = movesJson as MoveRecord[];
const ITEMS = itemsJson as ItemRecord[];
export const MACHINES: readonly MachineRecord[] = machinesJson as MachineRecord[];
export const MAPS: readonly MapRecord[] = mapsJson as MapRecord[];

export const NUM_SPECIES_INDICES = 190;

const byIndex: (SpeciesInfo | null)[] = new Array(256).fill(null);
const byDex: (SpeciesInfo | null)[] = new Array(152).fill(null);
for (const r of SPECIES_RECORDS) {
  if (!r.valid) continue;
  const { valid: _valid, ...info } = r;
  byIndex[r.index] = info;
  byDex[r.dex] = info;
}
const VALID_SPECIES: SpeciesInfo[] = byIndex.filter((s): s is SpeciesInfo => s !== null).sort((a, b) => a.dex - b.dex);

/** Species by internal index; null for MissingNo, glitch indices and anything outside 1–190. */
export function species(internalIndex: number): SpeciesInfo | null {
  if (!Number.isInteger(internalIndex) || internalIndex < 0 || internalIndex > 255) return null;
  return byIndex[internalIndex] ?? null;
}

/** Species by national dex number 1–151. */
export function speciesByDex(dex: number): SpeciesInfo | null {
  if (!Number.isInteger(dex) || dex < 1 || dex > 151) return null;
  return byDex[dex] ?? null;
}

/** All 151 valid species, in dex order. */
export function allSpecies(): SpeciesInfo[] {
  return VALID_SPECIES.slice();
}

export function isValidSpecies(internalIndex: number): boolean {
  return species(internalIndex) !== null;
}

const moveById = new Map(MOVES.map((m) => [m.id, m]));

export function moveInfo(id: number): { id: number; name: string; pp: number; type: number; power: number; effect: number; accuracy: number } | null {
  const m = moveById.get(id);
  return m ? { ...m } : null;
}

export function allMoves(): MoveRecord[] {
  return MOVES.map((m) => ({ ...m }));
}

const itemById = new Map(ITEMS.map((i) => [i.id, i]));

export function itemInfo(id: number): { id: number; name: string; price: number; rareIndex: number | null; machine: number | null } | null {
  const i = itemById.get(id);
  return i ? { ...i } : null;
}

export function allItems(): ItemRecord[] {
  return ITEMS.map((i) => ({ ...i }));
}

const mapByConst = new Map(MAPS.map((m) => [m.constName, m.id]));

export function mapId(constName: string): number | null {
  return mapByConst.get(constName) ?? null;
}

export function mapName(id: number): string | null {
  return MAPS.find((m) => m.id === id)?.constName ?? null;
}
